import React, { memo, useEffect, useRef } from 'react';
import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import ItemsGrid from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsGrid';
import BasicBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/BasicBtn';
import ContTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ContTitle';
import CategoryMenuBar from '@wconcept/wconcept-ui-renewal/components/organisms/_menuBar/CategoryMenuBar';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/src/components/atoms/ItemsSwiper';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import SwiperPagination from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperPagination';
import { Swiper as SwiperClass } from 'swiper/types';
import {
  classNameBind,
  getUniqueKey,
} from '@wconcept/wconcept-common/utils/commonUtils';
import { getResizeImageUrl } from '@wconcept/wconcept-common/utils/domainUtil';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  title?: string;
  subTitle?: string;
  imgSrc?: string;
  imgText?: string;
  imgSubText?: string;
  rs?: string;
  colorType?: ColorType;
  categoryTabList?: React.ReactNode[];
  selectedCateTabId?: string;
  productList?: React.ReactNode;
  isDesktop?: boolean;
  isLink?: boolean;
  useMoreButton?: boolean;
  onClickMore?: () => void;
}

const ProductSection = ({
  title,
  subTitle,
  imgSrc,
  imgText,
  imgSubText,
  rs,
  colorType = 'white',
  categoryTabList,
  selectedCateTabId,
  productList,
  isDesktop,
  isLink = false,
  useMoreButton,
  onClickMore,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const uniqueKey = React.useMemo(() => `swiper-navi-${getUniqueKey()}`, []);
  const swiperRef = useRef<SwiperClass>(null);
  // 상품 배열을 12개씩 묶는 함수
  const chunkArray = (arr: React.ReactNode[], size: number) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );
  const filteredChildren = React.Children.toArray(productList);
  const cName = classNameBind([
    'event-mega-product',
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);

  // productList가 변경될 때마다 Swiper 업데이트
  useEffect(() => {
    if (swiperRef.current && isDesktop && filteredChildren.length > 0) {
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update();

          // navigation 재초기화
          if (swiperRef.current.navigation) {
            swiperRef.current.navigation.destroy();
            swiperRef.current.navigation.init();
            swiperRef.current.navigation.update();
          }

          // pagination 재초기화
          if (swiperRef.current.pagination) {
            swiperRef.current.pagination.destroy();
            swiperRef.current.pagination.init();
            swiperRef.current.pagination.render();
            swiperRef.current.pagination.update();
          }
        }
      }, 0);
    }
  }, [productList, isDesktop, filteredChildren.length, uniqueKey]);

  return (
    <S.ProductSection
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
    >
      <ContTitle
        title={title}
        subTitle={subTitle}
        titleColor={customColors?.textColor}
        subTitleColor={customColors?.textColor}
      />

      {categoryTabList && (
        <CategoryMenuBar
          type="type7"
          bgType={!isCustomColor ? (colorType as 'black' | 'white') : undefined}
          items={categoryTabList}
          selectedValue={selectedCateTabId}
          className="category-tab"
        />
      )}

      {imgSrc && (
        <div className="img-wrap">
          <Img src={getResizeImageUrl(imgSrc, rs)} />
          {imgText && (
            <Text
              className="banner-text"
              weight={600}
              size={20}
              lineHeight="130%"
              color="#fff"
              block
            >
              {imgText}
            </Text>
          )}
          {imgSubText && (
            <Text
              className="banner-sub-text"
              size={14}
              lineHeight="130%"
              color="#fff"
              block
            >
              {imgSubText}
            </Text>
          )}
        </div>
      )}

      {isDesktop ? (
        <ItemsSwiper
          key={selectedCateTabId ?? 'default'}
          className="product-list"
          loop={false}
          autoplay={false}
          pagination={{
            el: `.${uniqueKey}-page`,
            type: 'fraction',
          }}
          paginationEl={
            filteredChildren.length > 12 ? (
              <SwiperPagination
                type="fraction02Medium"
                className={`${uniqueKey}-page`}
                bgType={colorType === 'black' ? 'black' : 'white'}
              />
            ) : undefined
          }
          navigation={{
            prevEl: `.${uniqueKey}-navi .swiper-button-prev`,
            nextEl: `.${uniqueKey}-navi .swiper-button-next`,
          }}
          navigationEl={
            filteredChildren.length > 12 ? (
              <SwiperNavigation
                type="bottom01"
                size="medium"
                bgType={colorType === 'black' ? 'black' : 'white'}
                className={`${uniqueKey}-navi`}
              />
            ) : undefined
          }
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
        >
          {chunkArray(filteredChildren, 12).map((group, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <ItemsGrid key={idx} perView={6} rowGap={40} colGap={18}>
              {group}
            </ItemsGrid>
          ))}
        </ItemsSwiper>
      ) : (
        <>
          <div className="product-list">
            <ItemsGrid perView={3} rowGap={24} colGap={6} md={{ perView: 3 }}>
              {productList}
            </ItemsGrid>
          </div>
          {useMoreButton && (
            <BasicBtn
              className="btn-section-more"
              text={isLink ? '상품 전체 보기' : '상품 더보기'}
              type={isLink ? 'line' : 'lineDropdown'}
              onClick={onClickMore}
            />
          )}
        </>
      )}
    </S.ProductSection>
  );
};

export default memo(ProductSection);
